import type { ProductType } from '@mevaser/shared'
import { ApiError, isUnitTracked, queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  STOCK_REQUEST_STATUS_TONE,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { ArrowLeftRight, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { SourceAvailability } from '../components/source-availability.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface Location {
  id: string
  key: string
  name: string
  isActive: boolean
}

interface ProductRow {
  id: string
  sku: string
  name: string
  type: ProductType
}

interface StockRequestRow {
  id: string
  requestNumber: string
  orderNumber: string | null
  status: string
  sourceLocationId: string
  sourceLocationName: string
  destinationLocationId: string
  destinationLocationName: string
  note: string | null
  itemCount: number
  hasShortfall: boolean
  createdAt: string
}

/**
 * Where stock is asked for between branches — "I need this, this and that
 * from the warehouse." A request from one location's point of view is
 * incoming (someone else wants stock from here) or outgoing (this location
 * did the asking); the same request shows up on both sides.
 */
export function StockRequestsPage() {
  const { can } = useAuth()
  // Not schema-validated: this route only ever receives these from the "Request
  // from another location" link on the till, which sets them all itself.
  const search = useSearch({ strict: false }) as {
    newRequest?: boolean
    productId?: string
    productName?: string
    productSku?: string
  }
  const [locationId, setLocationId] = useState('')
  const [creating, setCreating] = useState(false)

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
  })
  const activeLocations = (locations ?? []).filter((l) => l.isActive)

  const resolvedLocationId =
    locationId ||
    activeLocations.find((l) => l.key === 'storefront')?.id ||
    activeLocations[0]?.id ||
    ''

  const prefillItem =
    search.newRequest && search.productId && search.productName && search.productSku
      ? { productId: search.productId, name: search.productName, sku: search.productSku }
      : null

  // A "Request from another location" link on the till can land here already
  // knowing which product came up short.
  useEffect(() => {
    if (search.newRequest) setCreating(true)
  }, [search.newRequest])

  const incoming = useQuery({
    queryKey: queryKeys.stockRequests.list({
      locationId: resolvedLocationId,
      direction: 'incoming',
    }),
    queryFn: () =>
      api.get<{ items: StockRequestRow[] }>('/stock-requests', {
        query: { locationId: resolvedLocationId, direction: 'incoming', pageSize: 100 },
      }),
    enabled: Boolean(resolvedLocationId),
  })

  const outgoing = useQuery({
    queryKey: queryKeys.stockRequests.list({
      locationId: resolvedLocationId,
      direction: 'outgoing',
    }),
    queryFn: () =>
      api.get<{ items: StockRequestRow[] }>('/stock-requests', {
        query: { locationId: resolvedLocationId, direction: 'outgoing', pageSize: 100 },
      }),
    enabled: Boolean(resolvedLocationId),
  })

  return (
    <Page>
      <PageHeader
        title="Stock requests"
        description="Ask another location for stock, or see what your own location has been asked for."
        actions={
          can('inventory:request') ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden="true" /> New request
            </Button>
          ) : null
        }
      />

      <div className="mb-4 max-w-xs">
        <Field label="My location">
          {(props) => (
            <Select value={resolvedLocationId} onValueChange={setLocationId}>
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activeLocations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>

      <Tabs defaultValue="incoming">
        <TabsList>
          <TabsTrigger value="incoming">Asked of us</TabsTrigger>
          <TabsTrigger value="outgoing">We asked</TabsTrigger>
        </TabsList>

        <TabsContent value="incoming">
          <RequestList
            rows={incoming.data?.items ?? []}
            isLoading={incoming.isLoading}
            counterpartLabel="From"
            counterpartName={(r) => r.destinationLocationName}
            empty="Nobody has asked this location for stock."
          />
        </TabsContent>

        <TabsContent value="outgoing">
          <RequestList
            rows={outgoing.data?.items ?? []}
            isLoading={outgoing.isLoading}
            counterpartLabel="To"
            counterpartName={(r) => r.sourceLocationName}
            empty="This location has not asked anywhere for stock."
          />
        </TabsContent>
      </Tabs>

      <NewRequestDialog
        open={creating}
        destinationLocationId={resolvedLocationId}
        locations={activeLocations}
        prefillItem={prefillItem}
        onClose={() => setCreating(false)}
      />
    </Page>
  )
}

function RequestList({
  rows,
  isLoading,
  counterpartLabel,
  counterpartName,
  empty,
}: {
  rows: StockRequestRow[]
  isLoading: boolean
  counterpartLabel: string
  counterpartName: (row: StockRequestRow) => string
  empty: string
}) {
  if (isLoading) return null

  if (rows.length === 0) {
    return (
      <div className="mt-4">
        <EmptyState icon={ArrowLeftRight} title="Nothing here" description={empty} />
      </div>
    )
  }

  return (
    <ul className="mt-3 divide-y divide-border rounded-md border border-border">
      {rows.map((row) => (
        <li key={row.id}>
          <Link
            to="/stock-requests/$requestId"
            params={{ requestId: row.id }}
            className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-surface-sunken"
          >
            <div className="min-w-0">
              <p className="font-identifier font-medium">
                {row.requestNumber}
                {row.orderNumber ? (
                  <span className="ms-2 font-sans text-xs font-normal text-foreground-muted">
                    for {row.orderNumber}
                  </span>
                ) : null}
              </p>
              <p className="text-xs text-foreground-muted">
                {counterpartLabel} {counterpartName(row)} · {row.itemCount}{' '}
                {row.itemCount === 1 ? 'item' : 'items'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="text-xs text-foreground-muted">
                {new Date(row.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              {row.status === 'fulfilled' && row.hasShortfall ? (
                <Badge tone="warning">Partially fulfilled</Badge>
              ) : (
                <Badge tone={STOCK_REQUEST_STATUS_TONE[row.status] ?? 'neutral'}>
                  {humanizeStatus(row.status)}
                </Badge>
              )}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

interface DraftItem {
  key: string
  productId: string
  sku: string
  name: string
  qty: number
}

function NewRequestDialog({
  open,
  destinationLocationId,
  locations,
  prefillItem,
  onClose,
}: {
  open: boolean
  destinationLocationId: string
  locations: Location[]
  prefillItem: { productId: string; name: string; sku: string } | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [sourceLocationId, setSourceLocationId] = useState('')
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<DraftItem[]>([])
  const [note, setNote] = useState('')

  const otherLocations = locations.filter((l) => l.id !== destinationLocationId)

  // Fresh every time it opens — a cancelled draft must not leak into the next
  // one, except the item a "Request from another location" link arrived with.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately only resets on open, not on every keystroke or prop change while open
  useEffect(() => {
    if (!open) return
    setSourceLocationId((current) => current || otherLocations[0]?.id || '')
    setSearch('')
    setNote('')
    setItems(
      prefillItem
        ? [
            {
              key: crypto.randomUUID(),
              productId: prefillItem.productId,
              sku: prefillItem.sku,
              name: prefillItem.name,
              qty: 1,
            },
          ]
        : [],
    )
  }, [open])

  const { data: products } = useQuery({
    queryKey: queryKeys.products.list({ q: search, forStockRequest: true }),
    queryFn: () =>
      api.get<{ items: ProductRow[] }>('/products', {
        query: { q: search || undefined, isActive: true, pageSize: 20, sort: 'name', order: 'asc' },
      }),
    enabled: open,
  })

  // Plans and services carry no stock — nothing to ask for.
  const stockedProducts = (products?.items ?? []).filter(
    (p) => p.type === 'stock' || isUnitTracked(p.type),
  )

  const addProduct = (product: ProductRow) => {
    setItems((current) => {
      if (current.some((i) => i.productId === product.id)) return current
      return [
        ...current,
        {
          key: crypto.randomUUID(),
          productId: product.id,
          sku: product.sku,
          name: product.name,
          qty: 1,
        },
      ]
    })
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/stock-requests', {
        sourceLocationId,
        destinationLocationId,
        note: note.trim() || null,
        items: items.map((i) => ({ productId: i.productId, qty: i.qty, note: null })),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.stockRequests.all() })
      toast.success('Request sent')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not send the request',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const valid = sourceLocationId && sourceLocationId !== destinationLocationId && items.length > 0

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request stock from another location</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Ask">
            {(props) => (
              <Select value={sourceLocationId} onValueChange={setSourceLocationId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a location…" />
                </SelectTrigger>
                <SelectContent>
                  {otherLocations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the catalogue by name or SKU…"
            />
            {search.trim() ? (
              <ul className="mt-1 max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {stockedProducts.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                      onClick={() => addProduct(product)}
                    >
                      <span className="font-medium">{product.name}</span>{' '}
                      <span className="font-identifier text-xs text-foreground-muted">
                        {product.sku}
                      </span>
                    </button>
                  </li>
                ))}
                {stockedProducts.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-foreground-muted">No matches</li>
                ) : null}
              </ul>
            ) : null}
          </div>

          {items.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border border-border">
              {items.map((item) => (
                <li key={item.key} className="flex items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="font-identifier text-xs text-foreground-muted">{item.sku}</p>
                    {sourceLocationId ? (
                      <SourceAvailability
                        productId={item.productId}
                        sourceLocationId={sourceLocationId}
                        sourceName={
                          otherLocations.find((l) => l.id === sourceLocationId)?.name ??
                          'the chosen location'
                        }
                        qty={item.qty}
                      />
                    ) : null}
                  </div>
                  <Input
                    type="number"
                    min={1}
                    value={item.qty}
                    onChange={(e) =>
                      setItems((current) =>
                        current.map((i) =>
                          i.key === item.key
                            ? { ...i, qty: Math.max(1, Number(e.target.value) || 1) }
                            : i,
                        ),
                      )
                    }
                    className="w-16 text-end"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => setItems((current) => current.filter((i) => i.key !== item.key))}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-foreground-muted">Search above and add what's needed.</p>
          )}

          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything the other side should know"
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
